import type { ContentType } from "@brookbush/database";
import type { ContentItem } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { MinusIcon, PlusIcon } from "lucide-react";
import { usePlausible } from "next-plausible";
import { toast } from "react-hot-toast";
import useSWR from "swr";

import { showReferralToast } from "~/lib/toasts/showReferralToast";
import fetcher from "~/utils/fetcher.client";

type UseBookmarksProps =
	| {
			excludeContent?: boolean;
			contentType?: ContentType;
	  }
	| undefined;

const useBookmarks = ({
	contentType,
	excludeContent,
}: UseBookmarksProps = {}) => {
	const { isSignedIn } = useAuth();
	const queryString = new URLSearchParams({
		...(contentType ? { contentType } : {}),
		...(excludeContent ? { exclude: "content" } : {}),
	}).toString();

	const track = usePlausible();

	const { mutate, ...rest } = useSWR<ContentItem[]>(
		isSignedIn && `/bookmarks?${queryString}`,
	);

	const addBookmark = async (contentId: string) => {
		await mutate(
			fetcher(`/bookmarks`, {
				method: "POST",
				data: {
					contentId,
					contentType,
				},
			}),

			{
				populateCache: (newData: ContentItem, oldData) => {
					toast.success("Added To My List", {
						icon: <PlusIcon size={22} />,
					});
					showReferralToast();
					return [...oldData!, newData];
				},
			},
		);

		track("Bookmark", {
			props: {
				contentType,
			},
		});
	};

	const removeBookmark = async (id: number) => {
		await mutate(
			fetcher(`/bookmarks/${id}`, {
				method: "DELETE",
			}),
			{
				revalidate: false,
				populateCache: (newData: ContentItem, oldData) => {
					toast.success(`Removed From My List`, {
						icon: <MinusIcon size={24} />,
					});
					return oldData!.filter((bookmark) => bookmark.id !== newData.id);
				},
			},
		);
	};

	return {
		bookmarks: rest.data,
		addBookmark,
		removeBookmark,
		isLoadingBookmarks: rest.isLoading,
		...rest,
	};
};

export default useBookmarks;
